import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
  SortEndHandler,
} from 'react-sortable-hoc';
import clsx from 'clsx';

import { ROUTES } from '../../../routes';

// redux
import { connect } from 'react-redux';
import { mapDispatchProps, mapStateToProps } from '../../../store/store';
import { BRAND_BILLING_TYPES } from '../../../store/brand/reducer';

import { debounce } from '../../../helpers/debounce';
import OnAfterModalClosed from '../../../libraries/onAfterModalClosed';
import PaymentCreate from '../../payments/create';
import PaymentComplete from '../../payments/complete';

import BottomGradient from '../../../components/bottomGradient';
import Button from '../../../components/button';
import LoadingText from '../../../components/loadingText';

import './resourceOrderer.css';
import { useLanguage } from '@languages/index';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

interface Props {
  resourceName: string;
  resourceCount: number;
  fetcher: any;
  initialFetchCount: number;
  adderForMore: number;
  makeItemTagFunc?: any;
  updateOrderFunc: any;

  auth?: any;
  brand?: any;
  showModal?: any;
  closeModal?: any;
  showOverlays?: any;
  closeOverlays?: any;
}

interface SortableItemProps {
  itemTag: any;
}

interface SortableListProps {
  resources: any[];
  onSortEnd: SortEndHandler;
}

const DragHandle = SortableHandle(() => <span></span>);

const SortableItem = SortableElement<SortableItemProps>(({ itemTag }) => (
  <div className="sortable-item">
    {itemTag}
    <DragHandle />
  </div>
));

const SortableList = SortableContainer<SortableListProps>(({ resources }) => (
  <div className="grid grid-cols-3 grid-flow-row gap-[4px] lg:gap-[14px] w-full">
    {resources.map((rsrc, idx) => (
      <SortableItem
        key={`item-${rsrc.id}`}
        index={idx}
        itemTag={rsrc.itemTag}
      />
    ))}
  </div>
));

const ResourceOrderer = (props: Props) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.sns };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const onAfterModalClosed = new OnAfterModalClosed(props);

  const storeSnsPass = () => {
    navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH, {
      replace: true,
    });
  };

  const [fetchedResourceCount, setFetchedResourceCount] = useState(
    props.initialFetchCount,
  );
  const [isPublishedFiltered, setIsProvidedFiltered] = useState('true');
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // const [hasSpace, updateSpaceState] = useSpaceChecker();

  const [resources, setResources] = useState(null);
  const [resourceIndices, setResourceIndices] = useState([]);

  const fetchResourcesRes = useQuery({
    queryKey: [
      `fetch_resources:${props.resourceName}`,
      props.brand?.id,
      fetchedResourceCount,
    ],
    queryFn: async () => {
      try {
        setIsFetching(true);
        const resources = await props.fetcher.fetch(fetchedResourceCount);
        if (resources.length === 0) setIsFetching(false);
        return resources;
      } catch (error) {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts._index_text_4(props.resourceName),
        });
        setIsFetching(false);
      }
    },
    enabled: !!props.fetcher,
  });

  const removeFetchQueries = () => {
    queryClient.removeQueries({
      queryKey: [`fetch_resources:${props.resourceName}`],
      exact: false,
    });
  };

  useEffect(() => {
    return () => {
      removeFetchQueries();
    };
  }, []);

  useEffect(() => {
    // on fetchResourcesRes.data updated
    if (!fetchResourcesRes.data) return;

    // resources
    const resources = fetchResourcesRes.data;
    setResources(resources);

    // resourceIndices
    if (resourceIndices.length < resources.length) {
      const additional = [];
      for (let ri = resourceIndices.length; ri < resources.length; ++ri)
        additional.push(ri);
      setResourceIndices(resourceIndices.concat(additional));
    } else {
      const newIndices = [];
      for (let ri = 0; ri < resources.length; ++ri) newIndices.push(ri);
      setResourceIndices(newIndices);
    }

    // hasMore
    setHasMore(fetchedResourceCount < props.resourceCount);
    setIsFetching(false);
  }, [fetchResourcesRes.data]);

  useEffect(() => {
    if (!props.fetcher) return;

    setHasMore(fetchedResourceCount < props.resourceCount);
  }, [fetchedResourceCount]);

  // useEffect(() => {
  //   if (hasSpace && !isFetching && hasMore) {
  //     updateSpaceState();
  //     setFetchedResourceCount(fetchedResourceCount + props.adderForMore);
  //   }
  // }, [resources, hasSpace]);

  const renderLoadingIndicator = () => {
    return (
      <div
        className={clsx({
          'flex flex-row mx-10 my-10 justify-center items-center': true,
          'my-[60px]': resources && resources.length === 0,
          'my-[10px]': resources && resources.length > 0,
        })}
      >
        <span className="prose prose-xl text-slate-400">
          {languageTexts.loading_1}
        </span>
      </div>
    );
  };

  const showPaymentCompleteModal = (
    targetProductType,
    amount,
    price,
    priceTotal,
  ) => {
    setTimeout(() => {
      props.showOverlays({
        content: () => {
          return (
            <PaymentComplete
              // title={languageTexts.payment_history2}
              itemType={targetProductType}
              onConfirm={() => {
                props.closeOverlays();
              }}
            />
          );
        },
      });
    }, 400);
  };

  const proceedPayment = () => {
    let amount = undefined;
    let price = undefined;
    let priceTotal = undefined;

    props.closeOverlays();

    const targetProductType = props.brand?.hasExperiencedSnsInstall
      ? PAYMENT_PRODUCT_TYPE.SNS_PASS
      : PAYMENT_PRODUCT_TYPE.SNS_INSTALL;

    switch (targetProductType) {
      case PAYMENT_PRODUCT_TYPE.SNS_PASS:
        amount = 1;
        price = props.brand.priceSnsPass;
        priceTotal = null;
        break;

      case PAYMENT_PRODUCT_TYPE.SNS_INSTALL:
        amount = 1;
        price = props.brand.priceSnsPass;
        priceTotal = props.brand.priceSnsPass + props.brand.priceSnsInstall;
        break;
    }

    debounce(() => {
      if (
        props.brand.remainSnsPass ||
        props.brand.billingType == BRAND_BILLING_TYPES.FREE
      ) {
        // storeCampaign();
      } else {
        props.showOverlays({
          content: () => {
            return (
              <Elements stripe={stripePromise}>
                <PaymentCreate
                  itemType={targetProductType}
                  amount={amount}
                  price={price}
                  priceTotal={priceTotal}
                  onSuccess={() => {
                    showPaymentCompleteModal(
                      targetProductType,
                      amount,
                      price,
                      priceTotal,
                    );

                    storeSnsPass();
                  }}
                />
              </Elements>
            );
          },
        });
      }
    }, 400);
  };

  useEffect(() => {
    if (
      props.brand?.hasExperiencedSnsInstall &&
      !props.brand?.snsPassInstalledAt
    ) {
      navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH);
    }
  }, []);

  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (!props.fetcher || isFetching) return;

    const newIndices = resourceIndices.slice();
    const [target] = newIndices.splice(oldIndex, 1);
    newIndices.splice(newIndex, 0, target);

    setResourceIndices(newIndices);
  };

  const renderSnsSection = () => {
    if (!props.brand?.hasExperiencedSnsInstall) {
      return null;
    }

    if (!props.brand?.snsPassInstalledAt) {
      return null;
    }

    return (
      <div className="flex flex-col justify-center">
        <div className="w-full max-w-[1025px] my-[4.25rem] mx-auto">
          <div className="flex flex-row px-[1rem] lg:px-0">
            <span className="font-bold text-[1.375rem]">
              {languageTexts._resource_text_1(props.resourceName)}
            </span>
          </div>

          <div className="flex flex-row justify-between mt-[1.875rem] px-[1rem] lg:px-0">
            <div>
              <span className="text-[0.9375rem]">
                {languageTexts._resource_text_2(props.resourceName)}
              </span>
            </div>
          </div>

          <div className="flex flex-col my-[1.875rem] gap-[1.5rem]">
            <div className="flex flex-row px-[1rem] lg:px-0">
              {!isFetching && resources && resources.length == 0 ? (
                <div className="py-[12rem] mx-auto">
                  <span className="font-bold text-[1.375rem] text-[#E8E8E8]">
                    {languageTexts._resource_text_3(props.resourceName)}
                  </span>
                </div>
              ) : (
                <>
                  {resources && resources.length ? (
                    <SortableList
                      resources={resourceIndices.map((ri) => {
                        const rsrc = resources[ri];
                        rsrc.itemTag = props.makeItemTagFunc ? (
                          props.makeItemTagFunc(rsrc)
                        ) : (
                          <img
                            className="object-cover aspect-[0.7825] w-full h-full"
                            src={rsrc.images[0].thumbSmUrl}
                          />
                        );
                        return rsrc;
                      })}
                      onSortEnd={onSortEnd}
                      axis="xy"
                    />
                  ) : null}
                </>
              )}
            </div>

            {isFetching || !hasMore ? null : (
              <div className="flex flex-row px-[1rem] lg:px-0">
                <Button
                  className={'px-3 py-2 lg:py-3 text-center select-none'}
                  textColor={'black'}
                  buttonColor={'white'}
                  borderColor={'#888'}
                  text={languageTexts.show_more}
                  onClick={() => {
                    if (isFetching) return;
                    setFetchedResourceCount(
                      fetchedResourceCount + props.adderForMore,
                    );
                  }}
                />
              </div>
            )}

            {!isFetching ? null : renderLoadingIndicator()}

            <div className="mt-[2.25rem]"></div>
          </div>
        </div>
      </div>
    );
  };

  const saveOrder = async () => {
    if (isFetching) return;
    const resourceIds = resourceIndices.map((ri) => resources[ri].id);

    try {
      props.showModal({
        hideCloseButton: true,
        hideConfirmButton: true,
        content: () => {
          return (
            <div className="font-bold text-3xl text-white select-none">
              <LoadingText
                text={languageTexts.resource_text_4}
                render={(text) => {
                  return <span>{text}</span>;
                }}
              />
            </div>
          );
        },
      });
      await props.updateOrderFunc(resourceIds);

      props.closeModal();

      removeFetchQueries();
    } catch (error) {
      onAfterModalClosed.set(() => {
        props.showModal({
          hideCloseButton: true,
          text: languageTexts._resource_text_5(props.resourceName),
        });
      });
      props.closeModal();
    }
  };

  const renderSaveButton = () => {
    return (
      <div className="flex flex-col fixed w-full left-0 right-0 bottom-0 bg-red-400 justify-between">
        <div className="hidden md:flex">
          <BottomGradient />
        </div>

        <div className="fixed md:w-[240px] bottom-0 left-0 right-0 md:mx-auto form-submit-button md:rounded-sm md:mb-6 z-[2]">
          <Button
            className={'px-3 py-[22.5px] md:py-3 text-center'}
            textColor={'WHITE'}
            buttonColor={'BLACK'}
            borderColor={'BLACK'}
            text={languageTexts.resource_text_6}
            onClick={() => {
              saveOrder();
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {renderSnsSection()}
      {renderSaveButton()}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(ResourceOrderer);
